// ============================================================
// File: src/services/userService.js
// Purpose: Handle all User-related API calls
// Notes:
//  - Robust getUsers() which handles both `res.data.data` and `res.data` shapes
//  - Compatible with your existing backend endpoints
//  - Includes createUser, updateUser, getUsers, deleteUser, and tenant helpers
// Updated: 2025-11-05
// ============================================================

import axiosClient from "../api/axiosClient";

// ============================================================
// Create User
// Backend: POST /employees/create
// ============================================================
export const createUser = async (payload) => {
  try {
    const res = await axiosClient.post("/employees/create", payload);
    return res?.data || { success: false };
  } catch (err) {
    console.error("❌ createUser failed:", err);
    return { success: false, message: err?.response?.data?.message || err.message };
  }
};

// ============================================================
// Update User
// Backend: PUT /employees/<user_id>
// ============================================================
export const updateUser = async (userId, payload) => {
  try {
    const res = await axiosClient.put(`/employees/${userId}`, payload);
    return res?.data || { success: false };
  } catch (err) {
    console.error("❌ updateUser failed:", err);
    return { success: false, message: err?.response?.data?.message || err.message };
  }
};

// ============================================================
// Get Users (List Page)
// Backend: GET /employees/
// NOTE: tolerant to response shapes: { data: [...] } or directly [...]
// ============================================================
export const getUsers = async (params = {}) => {
  try {
    // allow optional params (e.g. ?q=... or ?tenant_id=...), axiosClient should handle object to querystring if configured,
    // otherwise build manually.
    let url = "/employees/";
    // if params is a plain object with keys, append query string
    if (params && Object.keys(params).length) {
      const qs = new URLSearchParams(params).toString();
      url = `${url}?${qs}`;
    }

    const res = await axiosClient.get(url);

    // handle both shapes:
    // 1) { status: 'success', data: [ ... ] }
    // 2) [ ... ]  (array)
    // 3) { data: { ... } } (object) - return that if needed
    const d = res?.data;
    if (Array.isArray(d)) return d;
    if (d && Array.isArray(d.data)) return d.data;
    // if backend returns object (single) return as single-element array or object as per your UI expectations
    if (d && typeof d === "object" && !Array.isArray(d.data) && Object.keys(d).length) {
      // UI expects array: attempt to return d.data if present, otherwise if d is an object of records return it in array
      // but safest: if d.data exists return it; else if d.results exists return it; else if contains entries return [d]
      if (d.data) return d.data;
      if (d.results) return d.results;
      // fallback: return array containing object (so UI won't crash)
      return [d];
    }
    return [];
  } catch (err) {
    console.error("❌ getUsers failed:", err);
    return [];
  }
};

// ============================================================
// Delete User
// Backend: DELETE /employees/<user_id>
// ============================================================
export const deleteUser = async (userId) => {
  try {
    const res = await axiosClient.delete(`/employees/${userId}`);
    return res?.data || { success: false };
  } catch (err) {
    console.error("❌ deleteUser failed:", err);
    return { success: false, message: err?.response?.data?.message || err.message };
  }
};

// ============================================================
// Get Active Tenants
// Backend: GET /tenant/api/list
// ============================================================
export const getActiveTenants = async () => {
  try {
    const res = await axiosClient.get("/tenant/api/list");
    // backend may return { data: [...] } or [...]
    const d = res?.data;
    if (Array.isArray(d)) return d;
    if (d?.data) return d.data;
    return [];
  } catch (err) {
    console.error("❌ getActiveTenants failed:", err);
    return [];
  }
};

// ============================================================
// Get Locations by Tenant
// Backend: GET /tenant/api/locations?tenant_id=...
// ============================================================
export const getLocationsByTenant = async (tenantId) => {
  try {
    if (!tenantId) return [];
    const res = await axiosClient.get(`/tenant/api/locations?tenant_id=${tenantId}`);
    const d = res?.data;
    if (Array.isArray(d)) return d;
    if (d?.data) return d.data;
    return [];
  } catch (err) {
    console.error("❌ getLocationsByTenant failed:", err);
    return [];
  }
};

// ============================================================
// Get Departments by Tenant
// Backend: GET /tenant/api/departments?tenant_id=...
// ============================================================
export const getDepartmentsByTenant = async (tenantId) => {
  try {
    if (!tenantId) return [];
    const res = await axiosClient.get(`/tenant/api/departments?tenant_id=${tenantId}`);
    const d = res?.data;
    if (Array.isArray(d)) return d;
    if (d?.data) return d.data;
    return [];
  } catch (err) {
    console.error("❌ getDepartmentsByTenant failed:", err);
    return [];
  }
};

// ============================================================
// Get Roles by Tenant
// Backend: GET /tenant/api/roles?tenant_id=...
// ============================================================
export const getRolesByTenant = async (tenantId) => {
  try {
    if (!tenantId) return [];
    const res = await axiosClient.get(`/tenant/api/roles?tenant_id=${tenantId}`);
    const d = res?.data;
    if (Array.isArray(d)) return d;
    if (d?.data) return d.data;
    return [];
  } catch (err) {
    console.error("❌ getRolesByTenant failed:", err);
    return [];
  }
};


// ✅ Get Single User by ID (safe add-on)
export const getUserById = async (userId) => {
  try {
    const res = await axiosClient.get(`/employees/${userId}`);
    return res?.data?.data || res?.data || null;
  } catch (err) {
    console.error("❌ getUserById failed:", err);
    return null;
  }
};
// ✅ Get Manager list

export const getManagersList = async () => {
  try {
    const res = await axiosClient.get("/employees/managers/list");
    return res?.data?.data || [];
  } catch (err) {
    console.error("❌ getManagersList failed:", err);
    return [];
  }
};



// ============================================================
// Default export
// ============================================================
export default {
  createUser,
  updateUser,
  deleteUser,
  getUsers,
  getUserById,   // ✅ add this line here
  getActiveTenants,
  getDepartmentsByTenant,
  getLocationsByTenant,
  getRolesByTenant,
};